"use client";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  Tooltip,
} from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";

interface SalesChartProps {
  title: string;
  subtitle: string;
  value: string;
  data: unknown[];
  type: "line" | "bar";
}

// Custom tooltip component for formatting values
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    // Get the full data point to access policies even if not charted as a line
    const dataPoint = payload[0]?.payload;
    return (
      <div className="bg-white p-3 border rounded shadow-lg">
        <p className="font-semibold text-gray-700 mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name === "premium" || entry.dataKey === "premium"
              ? `Premium: $${Number(entry.value).toLocaleString()}`
              : `Policies: ${entry.value}`}
          </p>
        ))}
        {/* Show policies if not already in payload (line chart only renders premium) */}
        {dataPoint?.policies !== undefined && !payload.some((e: any) => e.dataKey === "policies") && (
          <p className="text-sm text-gray-600">Policies: {dataPoint.policies}</p>
        )}
      </div>
    );
  }
  return null;
};

// Format Y-axis values as currency for premium
const formatYAxis = (value: number) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value}`;
};

// Utility function to ensure all 12 months are present in the data
const ensureAllMonths = (data: unknown[]): unknown[] => {
  // If no data, check if we should provide default structure
  if (!data || data.length === 0) {
    // Return empty array - let ensureMinimumData handle it
    return data || [];
  }

  // Check if this is monthly data (has 'month' key)
  const firstItem = data[0] as any;
  if (!firstItem?.month) return data; // Not monthly data, return as-is

  // All 12 months in short format
  const allMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Create a map of existing data by month name
  const dataMap = new Map<string, any>();
  data.forEach((item: any) => {
    if (item.month) {
      dataMap.set(item.month, item);
    }
  });

  // Fill in missing months with zero values
  const completeData = allMonths.map(monthName => {
    if (dataMap.has(monthName)) {
      return dataMap.get(monthName);
    }
    // Return a zero-value entry for missing months
    return {
      month: monthName,
      premium: 0,
      policies: 0,
    };
  });

  return completeData;
};

// Utility function to format week strings to readable date ranges
const formatWeekToDateRange = (weekValue: string | number): string => {
  // Convert to string if it's a number
  const weekString = String(weekValue);

  // Expected format: "Week 1", "Week 2", etc. or ISO week format
  // If it's already formatted (contains "-" or "/"), return as-is
  if (weekString.includes('-') || weekString.includes('/')) {
    return weekString;
  }

  // Extract week number from string like "Week 1", "Week 2", or just "1", "2"
  const weekMatch = weekString.match(/Week\s+(\d+)/i) || weekString.match(/^(\d+)$/);
  if (!weekMatch) {
    return weekString; // Return original if pattern doesn't match
  }

  const weekNumber = parseInt(weekMatch[1], 10);

  // Validate week number
  if (weekNumber < 1 || weekNumber > 53) {
    return weekString;
  }

  const currentYear = new Date().getFullYear();

  // Calculate the start date of the week using ISO 8601 week date system
  // Week 1 is the first week containing a Thursday (or containing January 4th)
  const jan4 = new Date(currentYear, 0, 4);
  const dayOfWeek = jan4.getDay() || 7; // Convert Sunday (0) to 7
  const week1Start = new Date(jan4);
  week1Start.setDate(jan4.getDate() - dayOfWeek + 1); // Monday of week 1

  // Calculate the start of the target week
  const weekStart = new Date(week1Start);
  weekStart.setDate(week1Start.getDate() + (weekNumber - 1) * 7);

  // Get week end (6 days after start = Sunday)
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  // Format dates - use compact format (M/D) for narrow charts
  const formatDate = (date: Date) => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}/${day}`;
  };

  return `${formatDate(weekStart)}-${formatDate(weekEnd)}`;
};

// Utility function to transform week data to use formatted date ranges
const transformWeekData = (data: unknown[]): unknown[] => {
  if (!data || data.length === 0) return data;

  const firstItem = data[0] as any;
  if (!firstItem?.week) return data; // Not weekly data, return as-is

  return data.map((item: any) => ({
    ...item,
    week: formatWeekToDateRange(item.week),
  }));
};

// Utility function to ensure minimum data for chart rendering
const ensureMinimumData = (data: unknown[], xAxisKey: string): unknown[] => {
  if (!data || data.length === 0) {
    // Provide placeholder data based on axis type
    if (xAxisKey === 'week') {
      // For weekly data, show a few placeholder weeks with formatted dates
      return [
        { week: formatWeekToDateRange('Week 1'), premium: 0, policies: 0 },
        { week: formatWeekToDateRange('Week 2'), premium: 0, policies: 0 },
        { week: formatWeekToDateRange('Week 3'), premium: 0, policies: 0 },
        { week: formatWeekToDateRange('Week 4'), premium: 0, policies: 0 },
      ];
    } else if (xAxisKey === 'month') {
      // For monthly data, show current year months
      return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        .map(month => ({ month, premium: 0, policies: 0 }));
    }
    // Default fallback
    return [{ [xAxisKey]: 'No Data', premium: 0, policies: 0 }];
  }
  return data;
};

export const SalesChart = function ({
  title,
  subtitle,
  value,
  data,
  type,
}: SalesChartProps) {
  const { branding } = useBranding();
  const primaryColor = branding.primaryColor || "#2563eb";
  const secondaryChartColor = branding.secondaryColor || primaryColor;

  // Apply the safety net to ensure all months are present
  let processedData = ensureAllMonths(data);

  // Automatically detect the X-axis key from data
  // Check for common keys: month, week, date, period
  const xAxisKey =
    processedData && processedData.length > 0
      ? (processedData[0] as any)?.month !== undefined ? 'month'
      : (processedData[0] as any)?.week !== undefined ? 'week'
      : (processedData[0] as any)?.date !== undefined ? 'date'
      : (processedData[0] as any)?.period !== undefined ? 'period'
      : 'month' // fallback
      : 'week'; // fallback for empty data - use week since that's common

  // Transform week data to use formatted date ranges
  if (xAxisKey === 'week') {
    processedData = transformWeekData(processedData);
  }

  // Ensure minimum data for chart rendering
  processedData = ensureMinimumData(processedData, xAxisKey);

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-sm font-medium text-gray-600">
            {title}
          </CardTitle>
          <div className="text-2xl font-bold text-primary mt-2">{value}</div>
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        </div>
        {/*<Select defaultValue="all-time">
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all-time">All time</SelectItem>
            <SelectItem value="last-month">Last month</SelectItem>
            <SelectItem value="last-week">Last week</SelectItem>
          </SelectContent>
        </Select>*/}
      </CardHeader>
      <CardContent>
        <div className="h-64 2xl:h-80">
          <ResponsiveContainer height="100%" width="100%" minHeight={200}>
            {type === "line" ? (
              <LineChart data={processedData}>
                <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" />
                <XAxis
                  axisLine={false}
                  dataKey={xAxisKey}
                  tick={{ fontSize: 10, fill: "#666" }}
                  tickLine={false}
                  interval={0}
                />
                <YAxis
                  yAxisId="left"
                  axisLine={false}
                  tick={{ fontSize: 12, fill: "#666" }}
                  tickLine={false}
                  tickFormatter={formatYAxis}
                  domain={[0, 'auto']}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  yAxisId="left"
                  dataKey="premium"
                  dot={{ fill: primaryColor, strokeWidth: 2, r: 4 }}
                  stroke={primaryColor}
                  strokeWidth={2}
                  type="monotone"
                  name="premium"
                />
              </LineChart>
            ) : (
              <BarChart data={processedData}>
                <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" />
                <XAxis
                  axisLine={false}
                  dataKey={xAxisKey}
                  tick={{ fontSize: 10, fill: "#666" }}
                  tickLine={false}
                  interval={0}
                />
                <YAxis
                  yAxisId="left"
                  axisLine={false}
                  tick={{ fontSize: 12, fill: "#666" }}
                  tickLine={false}
                  tickFormatter={formatYAxis}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  axisLine={false}
                  tick={{ fontSize: 12, fill: secondaryChartColor }}
                  tickLine={false}
                  domain={[0, 'auto']}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar yAxisId="left" dataKey="premium" fill={primaryColor} radius={[4, 4, 0, 0]} name="premium" />
                <Bar yAxisId="right" dataKey="policies" fill={secondaryChartColor} radius={[4, 4, 0, 0]} name="policies" />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
